import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
  Button,
  Tile,
  TextInput,
  Select,
  SelectItem,
  TextArea,
  FileUploader,
  Tag,
  ProgressIndicator,
  ProgressStep
} from '@carbon/react';
import {
  ArrowLeft,
  ArrowRight,
  Add,
  Close,
  Checkmark
} from '@carbon/icons-react';
import PageContainer from '../components/PageContainer';
import NativeDatePicker from '../components/NativeDatePicker';

const AddResident = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100';

  const [currentStep, setCurrentStep] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Responsive handler
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const steps = [
    { label: 'Osnovni podaci', description: 'Lični podaci korisnika' },
    { label: 'Medicinski podaci', description: 'Zdravstveno stanje' },
    { label: 'Ishrana', description: 'Dijeta i alergeni' },
    { label: 'Porodica', description: 'Kontakti staratelja' },
    { label: 'Finansije', description: 'Ugovor i cene' }
  ];

  // Form state
  const [formData, setFormData] = useState({
    // Osnovni podaci
    firstName: '',
    lastName: '',
    birthDate: '',
    gender: '',
    personalId: '',
    room: '',
    admissionDate: new Date().toISOString().split('T')[0], // Današnji datum
    careType: '',
    status: 'Aktivan',
    image: null,

    // Medicinski podaci
    diagnoses: [],
    therapy: [],
    allergies: [],
    
    // Ishrana
    dietType: '',
    dietDescription: '',
    dietRestrictions: [],
    foodAllergens: [],
    specialNotes: '',

    // Porodica / Staratelj
    primaryGuardianFirstName: '',
    primaryGuardianLastName: '',
    primaryGuardianRelation: '',
    primaryGuardianPhone: '',
    primaryGuardianEmail: '',
    additionalContacts: [],

    // Finansije
    contractNumber: '',
    packageType: '',
    pricePerDay: '',
    pricePerMonth: '',
    contractStartDate: '',
    contractEndDate: '',
    packageDescription: ''
  });

  const [tempDiagnosis, setTempDiagnosis] = useState('');
  const [tempTherapy, setTempTherapy] = useState({ medicine: '', dose: '', frequency: '', time: '' });
  const [tempAllergy, setTempAllergy] = useState({ allergen: '', reaction: '', severity: '' });
  const [tempRestriction, setTempRestriction] = useState('');
  const [tempFoodAllergen, setTempFoodAllergen] = useState('');
  const [tempContact, setTempContact] = useState({ name: '', relationship: '', phone: '' });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 0: // Osnovni podaci
        return formData.firstName && formData.lastName && formData.birthDate && 
               formData.gender && formData.personalId && formData.room && 
               formData.admissionDate && formData.careType;
      case 3: // Porodica
        return formData.primaryGuardianFirstName && formData.primaryGuardianLastName && 
               formData.primaryGuardianRelation && formData.primaryGuardianPhone;
      default:
        return true; // Ostali stepovi su opcioni (medicinski, ishrana, finansije)
    }
  };

  const addDiagnosis = () => {
    if (tempDiagnosis.trim()) {
      setFormData(prev => ({
        ...prev,
        diagnoses: [...prev.diagnoses, tempDiagnosis.trim()]
      }));
      setTempDiagnosis('');
    }
  };

  const removeDiagnosis = (index) => {
    setFormData(prev => ({
      ...prev,
      diagnoses: prev.diagnoses.filter((_, i) => i !== index)
    }));
  };

  const addTherapy = () => {
    if (tempTherapy.medicine && tempTherapy.dose) {
      setFormData(prev => ({
        ...prev,
        therapy: [...prev.therapy, { ...tempTherapy }]
      }));
      setTempTherapy({ medicine: '', dose: '', frequency: '', time: '' });
    }
  };

  const removeTherapy = (index) => {
    setFormData(prev => ({
      ...prev,
      therapy: prev.therapy.filter((_, i) => i !== index)
    }));
  };

  const addAllergy = () => {
    if (tempAllergy.allergen) {
      setFormData(prev => ({
        ...prev,
        allergies: [...prev.allergies, { ...tempAllergy }]
      }));
      setTempAllergy({ allergen: '', reaction: '', severity: '' });
    }
  };

  const removeAllergy = (index) => {
    setFormData(prev => ({
      ...prev,
      allergies: prev.allergies.filter((_, i) => i !== index)
    }));
  };

  const addRestriction = () => {
    if (tempRestriction.trim()) {
      setFormData(prev => ({
        ...prev,
        dietRestrictions: [...prev.dietRestrictions, tempRestriction.trim()]
      }));
      setTempRestriction('');
    }
  };

  const removeRestriction = (index) => {
    setFormData(prev => ({
      ...prev,
      dietRestrictions: prev.dietRestrictions.filter((_, i) => i !== index)
    }));
  };

  const addFoodAllergen = () => {
    if (tempFoodAllergen.trim()) {
      setFormData(prev => ({
        ...prev,
        foodAllergens: [...prev.foodAllergens, tempFoodAllergen.trim()]
      }));
      setTempFoodAllergen('');
    }
  };

  const removeFoodAllergen = (index) => {
    setFormData(prev => ({
      ...prev,
      foodAllergens: prev.foodAllergens.filter((_, i) => i !== index)
    }));
  };

  const addContact = () => {
    if (tempContact.name && tempContact.phone) {
      setFormData(prev => ({
        ...prev,
        additionalContacts: [...prev.additionalContacts, { ...tempContact }]
      }));
      setTempContact({ name: '', relationship: '', phone: '' });
    }
  };

  const removeContact = (index) => {
    setFormData(prev => ({
      ...prev,
      additionalContacts: prev.additionalContacts.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = () => {
    console.log('Form submitted:', formData);
    navigate('/residents');
  };

  // Step render functions
  const renderStep0 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '0.75rem' : '1rem' }}>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: isMobile ? '0.75rem' : '1rem' 
      }}>
        <TextInput
          id="firstName"
          labelText="Ime *"
          placeholder="Unesite ime"
          value={formData.firstName}
          onChange={(e) => handleInputChange('firstName', e.target.value)}
          required
        />
        <TextInput
          id="lastName"
          labelText="Prezime *"
          placeholder="Unesite prezime"
          value={formData.lastName}
          onChange={(e) => handleInputChange('lastName', e.target.value)}
          required
        />
      </div>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: isMobile ? '0.75rem' : '1rem' 
      }}>
        <NativeDatePicker
          id="birthDate"
          labelText="Datum rođenja *"
          value={formData.birthDate}
          onChange={(dates) => handleInputChange('birthDate', dates[0])}
          required
        />
        <Select
          id="gender"
          labelText="Pol *"
          value={formData.gender}
          onChange={(e) => handleInputChange('gender', e.target.value)}
          required
        >
          <SelectItem value="" text="Izaberite pol" />
          <SelectItem value="Muško" text="Muško" />
          <SelectItem value="Žensko" text="Žensko" />
        </Select>
      </div>
      <TextInput
        id="personalId"
        labelText="JMBG *"
        placeholder="Unesite JMBG"
        value={formData.personalId}
        onChange={(e) => handleInputChange('personalId', e.target.value)}
        required
      />
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: isMobile ? '0.75rem' : '1rem' 
      }}>
        <TextInput
          id="room"
          labelText="Soba *"
          placeholder="Broj sobe"
          value={formData.room}
          onChange={(e) => handleInputChange('room', e.target.value)}
          required
        />
        <NativeDatePicker
          id="admissionDate"
          labelText="Datum prijema *"
          value={formData.admissionDate}
          onChange={(dates) => handleInputChange('admissionDate', dates[0])}
          required
        />
      </div>
      <Select
        id="careType"
        labelText="Tip nege *"
        value={formData.careType}
        onChange={(e) => handleInputChange('careType', e.target.value)}
        required
      >
        <SelectItem value="" text="Izaberite tip nege" />
        <SelectItem value="Osnovna nega" text="Osnovna nega" />
        <SelectItem value="Visoka nega" text="Visoka nega" />
        <SelectItem value="Pojačana nega" text="Pojačana nega" />
        <SelectItem value="Palijativna nega" text="Palijativna nega" />
      </Select>
      <div style={{ marginTop: '0.5rem' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '0.75rem', 
          fontWeight: '500',
          marginBottom: '0.5rem',
          color: isDarkMode ? '#f4f4f4' : 'inherit'
        }}>
          Slika korisnika
        </label>
        <FileUploader
          labelTitle=""
          labelDescription="Samo JPG, PNG (maks 5MB)"
          buttonLabel="Dodaj sliku"
          filenameStatus="edit"
          accept={['.jpg', '.png', '.jpeg']}
          multiple={false}
          onChange={(e) => handleInputChange('image', e.target.files[0])}
        />
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '1rem' : '1.5rem' }}>
      <div>
        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: isDarkMode ? '#f4f4f4' : 'inherit' }}>
          Dijagnoze
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <TextInput
            id="tempDiagnosis"
            labelText=""
            placeholder="Unesite dijagnozu"
            value={tempDiagnosis}
            onChange={(e) => setTempDiagnosis(e.target.value)}
            style={{ flex: 1 }}
          />
          <Button kind="primary" size="md" renderIcon={Add} onClick={addDiagnosis}>Dodaj</Button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {formData.diagnoses.map((diagnosis, index) => (
            <Tag key={index} type="blue" filter onClose={() => removeDiagnosis(index)}>{diagnosis}</Tag>
          ))}
        </div>
      </div>
      <div>
        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: isDarkMode ? '#f4f4f4' : 'inherit' }}>
          Terapija
        </label>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '0.5rem', 
          marginBottom: '0.5rem' 
        }}>
          <TextInput id="tempMedicine" labelText="" placeholder="Lek" value={tempTherapy.medicine} onChange={(e) => setTempTherapy({...tempTherapy, medicine: e.target.value})} />
          <TextInput id="tempDose" labelText="" placeholder="Doza" value={tempTherapy.dose} onChange={(e) => setTempTherapy({...tempTherapy, dose: e.target.value})} />
          <TextInput id="tempFrequency" labelText="" placeholder="Učestalost" value={tempTherapy.frequency} onChange={(e) => setTempTherapy({...tempTherapy, frequency: e.target.value})} />
          <TextInput id="tempTime" labelText="" placeholder="Vreme" value={tempTherapy.time} onChange={(e) => setTempTherapy({...tempTherapy, time: e.target.value})} />
        </div>
        <Button kind="primary" size="md" renderIcon={Add} onClick={addTherapy} style={{ marginBottom: '0.5rem' }}>Dodaj terapiju</Button>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {formData.therapy.map((item, index) => (
            <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', padding: '0.625rem', backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)', borderRadius: '4px', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
              <span style={{ fontSize: '0.8125rem', wordBreak: 'break-word', flex: 1 }}>{item.medicine} - {item.dose} {item.frequency && `(${item.frequency})`} {item.time && `- ${item.time}`}</span>
              <Button kind="ghost" size="sm" renderIcon={Close} hasIconOnly iconDescription="Ukloni" onClick={() => removeTherapy(index)} />
            </div>
          ))}
        </div>
      </div>
      <div>
        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: isDarkMode ? '#f4f4f4' : 'inherit' }}>
          Alergije
        </label>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '0.5rem', 
          marginBottom: '0.5rem' 
        }}>
          <TextInput id="tempAllergen" labelText="" placeholder="Alergen" value={tempAllergy.allergen} onChange={(e) => setTempAllergy({...tempAllergy, allergen: e.target.value})} />
          <TextInput id="tempReaction" labelText="" placeholder="Reakcija" value={tempAllergy.reaction} onChange={(e) => setTempAllergy({...tempAllergy, reaction: e.target.value})} />
          <Select id="tempSeverity" labelText="" value={tempAllergy.severity} onChange={(e) => setTempAllergy({...tempAllergy, severity: e.target.value})}>
            <SelectItem value="" text="Ozbiljnost" />
            <SelectItem value="Niska" text="Niska" />
            <SelectItem value="Srednja" text="Srednja" />
            <SelectItem value="Visoka" text="Visoka" />
          </Select>
        </div>
        <Button kind="primary" size="md" renderIcon={Add} onClick={addAllergy} style={{ marginBottom: '0.5rem' }}>Dodaj alergiju</Button>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {formData.allergies.map((item, index) => (
            <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', padding: '0.625rem', backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)', borderRadius: '4px', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
              <span style={{ fontSize: '0.8125rem', wordBreak: 'break-word', flex: 1 }}>{item.allergen} {item.reaction && `- ${item.reaction}`} {item.severity && `(${item.severity})`}</span>
              <Button kind="ghost" size="sm" renderIcon={Close} hasIconOnly iconDescription="Ukloni" onClick={() => removeAllergy(index)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '0.75rem' : '1rem' }}>
      <Select id="dietType" labelText="Tip dijete" value={formData.dietType} onChange={(e) => handleInputChange('dietType', e.target.value)}>
        <SelectItem value="" text="Izaberite tip dijete" />
        <SelectItem value="Standardna ishrana" text="Standardna ishrana" />
        <SelectItem value="Dijabetičarska ishrana" text="Dijabetičarska ishrana" />
        <SelectItem value="Niskokalorična ishrana" text="Niskokalorična ishrana" />
        <SelectItem value="Meka hrana" text="Meka hrana" />
        <SelectItem value="Tečna ishrana" text="Tečna ishrana" />
      </Select>
      <TextArea id="dietDescription" labelText="Opis dijete" placeholder="Napomene o ishrani..." value={formData.dietDescription} onChange={(e) => handleInputChange('dietDescription', e.target.value)} rows={3} />
      <div>
        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: isDarkMode ? '#f4f4f4' : 'inherit' }}>
          Prehrambena ograničenja
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <TextInput id="tempRestriction" labelText="" placeholder="Npr. Bez šećera..." value={tempRestriction} onChange={(e) => setTempRestriction(e.target.value)} style={{ flex: 1 }} />
          <Button kind="primary" size="md" renderIcon={Add} onClick={addRestriction}>Dodaj</Button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {formData.dietRestrictions.map((restriction, index) => (
            <Tag key={index} type="red" filter onClose={() => removeRestriction(index)}>{restriction}</Tag>
          ))}
        </div>
      </div>
      <div>
        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: isDarkMode ? '#f4f4f4' : 'inherit' }}>
          Alergeni u hrani
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <TextInput id="tempFoodAllergen" labelText="" placeholder="Npr. Kikiriki..." value={tempFoodAllergen} onChange={(e) => setTempFoodAllergen(e.target.value)} style={{ flex: 1 }} />
          <Button kind="primary" size="md" renderIcon={Add} onClick={addFoodAllergen}>Dodaj</Button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {formData.foodAllergens.map((allergen, index) => (
            <Tag key={index} type="magenta" filter onClose={() => removeFoodAllergen(index)}>{allergen}</Tag>
          ))}
        </div>
      </div>
      <TextArea id="specialNotes" labelText="Specijalne napomene" placeholder="Dodatne napomene..." value={formData.specialNotes} onChange={(e) => handleInputChange('specialNotes', e.target.value)} rows={3} />
    </div>
  );

  const renderStep3 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '0.75rem' : '1rem' }}>
      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.8125rem', fontWeight: '600' }}>Primarni staratelj</h4>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '1rem' 
      }}>
        <TextInput id="primaryGuardianFirstName" labelText="Ime *" placeholder="Ime staratelja" value={formData.primaryGuardianFirstName} onChange={(e) => handleInputChange('primaryGuardianFirstName', e.target.value)} required />
        <TextInput id="primaryGuardianLastName" labelText="Prezime *" placeholder="Prezime staratelja" value={formData.primaryGuardianLastName} onChange={(e) => handleInputChange('primaryGuardianLastName', e.target.value)} required />
      </div>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '1rem' 
      }}>
        <TextInput id="primaryGuardianRelation" labelText="Srodstvo *" placeholder="Npr. Sin, Kćerka..." value={formData.primaryGuardianRelation} onChange={(e) => handleInputChange('primaryGuardianRelation', e.target.value)} required />
        <TextInput id="primaryGuardianPhone" labelText="Telefon *" placeholder="+381 64 123 4567" value={formData.primaryGuardianPhone} onChange={(e) => handleInputChange('primaryGuardianPhone', e.target.value)} required />
      </div>
      <TextInput id="primaryGuardianEmail" labelText="Email" placeholder="email@example.com" value={formData.primaryGuardianEmail} onChange={(e) => handleInputChange('primaryGuardianEmail', e.target.value)} />
      <hr style={{ border: 'none', borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, margin: '1rem 0' }} />
      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.8125rem', fontWeight: '600' }}>Dodatni kontakti</h4>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', 
        gap: '0.5rem', 
        marginBottom: '0.5rem' 
      }}>
        <TextInput id="tempContactName" labelText="" placeholder="Ime i prezime" value={tempContact.name} onChange={(e) => setTempContact({...tempContact, name: e.target.value})} />
        <TextInput id="tempContactRelationship" labelText="" placeholder="Srodstvo" value={tempContact.relationship} onChange={(e) => setTempContact({...tempContact, relationship: e.target.value})} />
        <TextInput id="tempContactPhone" labelText="" placeholder="Telefon" value={tempContact.phone} onChange={(e) => setTempContact({...tempContact, phone: e.target.value})} />
      </div>
      <Button kind="primary" size="md" renderIcon={Add} onClick={addContact} style={{ marginBottom: '0.5rem' }}>Dodaj kontakt</Button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {formData.additionalContacts.map((contact, index) => (
          <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', padding: '0.625rem', backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)', borderRadius: '4px', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
            <span style={{ fontSize: '0.8125rem', wordBreak: 'break-word', flex: 1 }}>{contact.name} {contact.relationship && `(${contact.relationship})`} - {contact.phone}</span>
            <Button kind="ghost" size="sm" renderIcon={Close} hasIconOnly iconDescription="Ukloni" onClick={() => removeContact(index)} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '0.75rem' : '1rem' }}>
      <TextInput id="contractNumber" labelText="Broj ugovora" placeholder="UGV-2024-XXXXX" value={formData.contractNumber} onChange={(e) => handleInputChange('contractNumber', e.target.value)} />
      <Select id="packageType" labelText="Tip paketa" value={formData.packageType} onChange={(e) => handleInputChange('packageType', e.target.value)}>
        <SelectItem value="" text="Izaberite tip paketa" />
        <SelectItem value="Standard" text="Standard" />
        <SelectItem value="Premium" text="Premium" />
        <SelectItem value="Pojačana nega" text="Pojačana nega" />
        <SelectItem value="Palijativna nega" text="Palijativna nega" />
      </Select>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: isMobile ? '0.75rem' : '1rem' 
      }}>
        <TextInput id="pricePerDay" labelText="Cena po danu (RSD)" placeholder="4500" value={formData.pricePerDay} onChange={(e) => handleInputChange('pricePerDay', e.target.value)} />
        <TextInput id="pricePerMonth" labelText="Cena po mesecu (RSD)" placeholder="135000" value={formData.pricePerMonth} onChange={(e) => handleInputChange('pricePerMonth', e.target.value)} />
      </div>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: isMobile ? '0.75rem' : '1rem' 
      }}>
        <TextInput id="contractStartDate" labelText="Početak ugovora" placeholder="DD.MM.GGGG" value={formData.contractStartDate} onChange={(e) => handleInputChange('contractStartDate', e.target.value)} />
        <TextInput id="contractEndDate" labelText="Kraj ugovora" placeholder="DD.MM.GGGG" value={formData.contractEndDate} onChange={(e) => handleInputChange('contractEndDate', e.target.value)} />
      </div>
      <TextArea id="packageDescription" labelText="Opis paketa" placeholder="Šta paket uključuje..." value={formData.packageDescription} onChange={(e) => handleInputChange('packageDescription', e.target.value)} rows={3} />
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderStep0();
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      default: return null;
    }
  };

  return (
    <PageContainer>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '0.75rem', 
        marginBottom: isMobile ? '0.75rem' : '1.5rem'
      }}>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={ArrowLeft}
          hasIconOnly
          iconDescription="Nazad"
          onClick={() => navigate('/residents')}
        />
        <h1 style={{ 
          fontSize: isMobile ? '1.125rem' : '1.5rem', 
          fontWeight: '500',
          margin: 0,
          color: isDarkMode ? '#f4f4f4' : 'inherit'
        }}>
          Dodaj novog korisnika
        </h1>
      </div>

      {/* Progress indicator - desktop top, mobile bottom fixed */}
      {!isMobile && (
        <div style={{ marginBottom: '2rem' }}>
          <ProgressIndicator currentIndex={currentStep} spaceEqually>
            {steps.map((step, index) => (
              <ProgressStep 
                key={index} 
                label={step.label} 
                description={step.description} 
              />
            ))}
          </ProgressIndicator>
        </div>
      )}

      <Tile style={{ 
        padding: isMobile ? '0.75rem' : '2rem', 
        marginBottom: isMobile ? '100px' : '2rem' // Space for fixed bottom bar on mobile
      }}>
        <h2 style={{ 
          margin: isMobile ? '0 0 0.75rem 0' : '0 0 1.5rem 0', 
          fontSize: isMobile ? '0.9375rem' : '1.25rem', 
          fontWeight: '500', 
          color: isDarkMode ? '#f4f4f4' : 'inherit' 
        }}>
          {steps[currentStep].label}
        </h2>
        {renderCurrentStep()}
      </Tile>

      {/* Navigation buttons - desktop inline, mobile fixed bottom */}
      {isMobile ? (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: isDarkMode ? '#161616' : '#ffffff',
          borderTop: `1px solid ${isDarkMode ? '#393939' : '#e0e0e0'}`,
          padding: '0.75rem 1rem',
          zIndex: 1000,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
        }}>
          {/* Mini progress indicator */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: '0.5rem',
            marginBottom: '0.75rem',
            fontSize: '0.75rem',
            color: isDarkMode ? '#c6c6c6' : '#525252'
          }}>
            <span style={{ fontWeight: '500' }}>Korak {currentStep + 1} / {steps.length}</span>
            <span>•</span>
            <span>{steps[currentStep].label}</span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button 
              kind="secondary" 
              size="sm" 
              onClick={prevStep} 
              disabled={currentStep === 0}
              style={{ flex: 1 }}
            >
              Nazad
            </Button>
            {currentStep < steps.length - 1 ? (
              <Button 
                kind="primary" 
                size="sm" 
                renderIcon={ArrowRight} 
                onClick={nextStep} 
                disabled={!isStepValid()}
                style={{ flex: 2 }}
              >
                Sledeći
              </Button>
            ) : (
              <Button 
                kind="primary" 
                size="sm" 
                renderIcon={Checkmark} 
                onClick={handleSubmit} 
                disabled={!isStepValid()}
                style={{ flex: 2 }}
              >
                Dodaj
              </Button>
            )}
            <Button 
              kind="ghost" 
              size="sm" 
              renderIcon={Close}
              hasIconOnly
              iconDescription="Otkaži"
              onClick={() => navigate('/residents')}
            />
          </div>
        </div>
      ) : (
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'space-between'
        }}>
          <Button 
            kind="secondary" 
            size="lg" 
            onClick={prevStep} 
            disabled={currentStep === 0}
          >
            Nazad
          </Button>
          <div style={{ 
            display: 'flex', 
            gap: '1rem'
          }}>
            <Button 
              kind="secondary" 
              size="lg" 
              onClick={() => navigate('/residents')}
            >
              Otkaži
            </Button>
            {currentStep < steps.length - 1 ? (
              <Button 
                kind="primary" 
                size="lg" 
                renderIcon={ArrowRight} 
                onClick={nextStep} 
                disabled={!isStepValid()}
              >
                Sledeći korak
              </Button>
            ) : (
              <Button 
                kind="primary" 
                size="lg" 
                renderIcon={Checkmark} 
                onClick={handleSubmit} 
                disabled={!isStepValid()}
              >
                Dodaj korisnika
              </Button>
            )}
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default AddResident;
